import { useMemo } from 'react'

import type { ChartDerivedMeasure, ChartMeasure, ChartMetricsConfigs, ChartPlugin, ChartWordMeasure, WarnAreasData } from '../_types'

interface UseChartMeasuresArgs extends ChartMetricsConfigs {
  assetId: string
  plugins: ChartPlugin[]
}

function dedupeKeepLastByKey<T, K extends PropertyKey>(items: T[], keyFn: (item: T) => K): T[] {
  const map = new Map<K, T>()
  for (const item of items) {
    const key = keyFn(item)
    // If the key already exists, delete+set so the "winner" is also last in output ordering.
    if (map.has(key)) { map.delete(key); }
    map.set(key, item)
  }
  return Array.from(map.values())
}

/**
 * Joins "base" chart measures props (coming from the widget props) with any additions
 * provided by plugins. Plugins may contribute measures, derived measures, binary words,
 * warn areas, and/or a React component to render.
 */
function useResolvedChartMetrics(args: UseChartMeasuresArgs): Required<ChartMetricsConfigs> {
  const { measures, binaryWords, warnAreas, derivedMeasures, plugins } = args

  const base = useMemo(
    () => ({
      measures: measures ?? [],
      binaryWords: binaryWords ?? [],
      warnAreas: warnAreas ?? [],
      derivedMeasures: derivedMeasures ?? [],
    }),
    [measures, binaryWords, warnAreas, derivedMeasures]
  )

  const pluginAdditions = useMemo(() => {
    const pluginMeasures = plugins.reduce<ChartMeasure[]>((acc, p) => acc.concat(p.measures ?? []), [])
    const pluginBinaryWords = plugins.reduce<ChartWordMeasure[]>(
      (acc, p) => acc.concat(p.binaryWords ?? []),
      []
    )
    const pluginWarnAreas = plugins.reduce<WarnAreasData[]>((acc, p) => acc.concat(p.warnAreas ?? []), [])
    const pluginDerivedMeasures = plugins.reduce<ChartDerivedMeasure[]>(
      (acc, p) => acc.concat(p.derivedMeasures ?? []),
      []
    )

    return {
      measures: pluginMeasures,
      binaryWords: pluginBinaryWords,
      warnAreas: pluginWarnAreas,
      derivedMeasures: pluginDerivedMeasures,
    }
  }, [plugins])

  return useMemo(
    () => ({
      // Plugins first, then base (manual/widget-provided). If slugs overlap, the last inserted wins.
      measures: dedupeKeepLastByKey([...pluginAdditions.measures, ...base.measures], m => m.slug),
      binaryWords: dedupeKeepLastByKey([...pluginAdditions.binaryWords, ...base.binaryWords], w => w.type),
      warnAreas: dedupeKeepLastByKey([...pluginAdditions.warnAreas, ...base.warnAreas], w => w.slug),
      derivedMeasures: dedupeKeepLastByKey([...pluginAdditions.derivedMeasures, ...base.derivedMeasures], m => m.slug),
    }),
    [base, pluginAdditions]
  )
}

export { useResolvedChartMetrics }